import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import { resetPayload, setForm as setGlobalForm, setPayload } from '../store/form.slice'
import { NavBack } from '../components/nav-back'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { SectionWizard } from '../components/forms/section-wizard'
import { QuestionWizard } from '../components/forms/question-wizard'
import {
  capitalize,
  captureScreenshot,
  formatNum,
  isFormDirty,
  parseDate
} from '../utils/functions'
import { TEXT_AREA_ROWS, TEXT_AREA_ROWS_MIN } from '../utils/constants'
import { FormSchema, QuestionSchema, SectionSchema } from '../utils/types/form'
import palettesData from '../data/palettes.json'
import formTipsData from '../data/form-tips.json'

// TODO set isEditingForm on page refresh
// TODO contenteditable for names, descriptions

export const NewFormView = () => {
  const [isEditingHeader, setIsEditingHeader] = useState({
    name: false,
    description: false
  })
  const [isFormOptionsOpen, setIsFormOptionsOpen] = useState(false)
  const [isPaletteOpen, setIsPaletteOpen] = useState(false)
  const [selectedSectionId, setSelectedSectionId] = useState('')
  const [selectedQuestionId, setSelectedQuestionId] = useState('')
  const [newSectionIndex, setNewSectionIndex] = useState(0)
  const [newQuestionIndex, setNewQuestionIndex] = useState(0)
  const [isSecondaryContextMenuOpen, setIsSecondaryContextMenuOpen] = useState(false)
  const [isSectionWizardOpen, setIsSectionWizardOpen] = useState(false)
  const [isQuestionWizardOpen, setIsQuestionWizardOpen] = useState(false)
  const [allQuestions, setAllQuestions] = useState<QuestionSchema[]>([])
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [formTips, setFormTips] = useState(formTipsData)
  const [isLoading, setIsLoading] = useState(false)
  const [requestState, setRequestState] = useState({ warning: '', error: '' })
  const sumCheckboxQuestions = useRef(0)
  const formRef = useRef<HTMLFormElement | null>(null)
  const formOptionsTriggerRef = useRef(null)
  const formOptionsContextRef = useRef(null)
  const paletteTriggerRef = useRef(null)
  const paletteContextRef = useRef(null)
  const selectedSectionTriggerRef = useRef(null)
  const selectedSectionContextRef = useRef(null)
  const selectedQuestionTriggerRef = useRef(null)
  const selectedQuestionContextRef = useRef(null)

  useOutsideClick({ element: formOptionsContextRef, trigger: formOptionsTriggerRef }, () =>
    setIsFormOptionsOpen(false)
  )

  useOutsideClick({ element: paletteContextRef, trigger: paletteTriggerRef }, () =>
    setIsPaletteOpen(false)
  )

  useOutsideClick({ element: selectedSectionContextRef, trigger: selectedSectionTriggerRef }, () =>
    setSelectedSectionId('')
  )

  useOutsideClick(
    {
      element: selectedQuestionContextRef,
      trigger: selectedQuestionTriggerRef
    },
    () => resetSelectedQuestion()
  )

  const dispatch = useAppDispatch()
  const formState = useAppSelector((state) => state.form)
  const { data: globalForms, payload, form } = formState
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { formId } = useParams()
  const [searchParams] = useSearchParams()
  const actionQuery = searchParams.get('action')
  const isAddingTemplate = actionQuery === '__addTemplate'
  const isEditingTemplate = actionQuery === '__editTemplate'

  const isNewForm = pathname.includes('new')

  useEffect(() => {
    if (authUser) {
      dispatch(
        setPayload({
          ...payload,
          meta: { ...payload.meta, isEditingForm: !isNewForm }
        })
      )
    }
    // eslint-disable-next-line
  }, [authUser])

  useEffect(() => {
    const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))

    if (!payload.form.description) {
      updatedForm.description = 'Add description...'
    }

    setPaletteValue(payload.form.meta.colour, true)
    dispatch(setPayload({ ...payload, form: updatedForm }))

    document.addEventListener('keyup', (e) => captureScreenshot(e, formRef, [], 405))

    return () => {
      document.removeEventListener('keyup', (e) => captureScreenshot(e, formRef, [], 405))
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (formId && !isNewForm) {
      populateData(formId)
    }
    // eslint-disable-next-line
  }, [formId])

  useEffect(() => {
    const allQuestions = payload.form.sections
      .filter(({ questions }) => questions)
      .flatMap(({ questions }) => questions)

    setAllQuestions(allQuestions)
  }, [payload.form])

  const populateData = async (formId: string) => {
    const existingForm = form?._id === formId ? form : globalForms.find(({ _id }) => _id === formId)

    /* let payloadForm */

    try {
      if (existingForm) {
        dispatch(setGlobalForm(existingForm))
        /* payloadForm = existingForm */
      } else {
        setIsLoading(true)
        /* payloadForm = await dispatch(fetchForm(formId)).unwrap() */
      }

      // TODO load form to payload on refresh
      /* dispatch(
        setPayload({
          ...payload,
          form: payloadForm,
          meta: {
            ...payload.meta,
            isEditingForm: !isNewForm
          }
        })
      ) */
    } catch (error: any) {
      const errorObj = JSON.parse(error.message)
      if (errorObj.status === 403 || errorObj.status === 404) {
        setRequestState({ ...requestState, error: 'no_access' })
      } else {
        setRequestState({
          ...requestState,
          error: 'Error encountered processing the request'
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.value) {
      dispatch(
        setPayload({
          ...payload,
          form: { ...payload.form, [e.target.id]: e.target.value }
        })
      )
    } else {
      if (e.target.id === 'name') {
        dispatch(
          setPayload({
            ...payload,
            form: { ...payload.form, name: 'Untitled form' }
          })
        )
      } else if (e.target.id === 'description') {
        dispatch(
          setPayload({
            ...payload,
            form: { ...payload.form, description: 'Add description...' }
          })
        )
      }
    }
  }

  const handleFormIsPublic = (e: React.ChangeEvent<HTMLInputElement>) => {
    const metaOptions = { ...payload.meta }

    if (!e.target.checked && metaOptions.publish) {
      metaOptions.publish = false
    }

    dispatch(
      setPayload({
        ...payload,
        form: { ...payload.form, isPublic: e.target.id === 'isPublic' },
        meta: { ...metaOptions }
      })
    )
  }

  const handlePublishForm = () => {
    dispatch(
      setPayload({
        ...payload,
        form: { ...payload.form, publishedOn: !payload.form.publishedOn }
      })
    )
  }

  const setPaletteValue = (name: string, initial: boolean = false) => {
    if (!initial) {
      removeThemeClass()
    }

    addThemeClass(name)

    dispatch(
      setPayload({
        ...payload,
        form: {
          ...payload.form,
          meta: { ...payload.form.meta, colour: name }
        }
      })
    )
  }

  const removeThemeClass = () => {
    const currentClass = `theme-content-${payload.form.meta.colour}`
    formRef.current?.classList.remove(currentClass)
  }

  const addThemeClass = (name: string) => {
    const newClass = `theme-content-${name}`
    formRef.current?.classList.add(newClass)
  }

  const handleSelectSection = (sectionId: string) => {
    selectedSectionId
      ? selectedSectionId === sectionId
        ? setSelectedSectionId('')
        : setSelectedSectionId(sectionId)
      : setSelectedSectionId(sectionId)
  }

  const handleSelectQuestion = (questionId: string) => {
    selectedQuestionId
      ? selectedQuestionId === questionId
        ? setSelectedQuestionId('')
        : setSelectedQuestionId(questionId)
      : setSelectedQuestionId(questionId)
  }

  const handleAddSection = () => {
    setIsSectionWizardOpen(true)
    setNewSectionIndex(payload.form.sections.length)
  }

  const handleAddQuestion = (section: SectionSchema) => {
    setIsQuestionWizardOpen(true)
    setSelectedSectionId(section.id)
    setNewQuestionIndex(section.questions.length)
  }

  const handleRequireQuestion = (questionId: string) => {
    const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))

    updatedForm.sections.forEach(({ questions }, sectionIndex) => {
      questions.forEach(({ id, required }, questionIndex) => {
        if (id === questionId) {
          updatedForm.sections[sectionIndex].questions[questionIndex].required = !required
        }
      })
    })

    dispatch(setPayload({ ...payload, form: updatedForm }))
  }

  const handleInsertSectionAbove = (index: number) => {
    setIsSectionWizardOpen(true)
    setSelectedSectionId('')
    setNewSectionIndex(index)
  }

  const handleInsertSectionBelow = (index: number) => {
    setIsSectionWizardOpen(true)
    setSelectedSectionId('')
    setNewSectionIndex(index + 1)
  }

  const handleInsertQuestionAbove = (sectionId: string, index: number) => {
    setIsQuestionWizardOpen(true)
    setSelectedSectionId(sectionId)
    setSelectedQuestionId('')
    setNewQuestionIndex(index)
  }

  const handleInsertQuestionBelow = (sectionId: string, index: number) => {
    setIsQuestionWizardOpen(true)
    setSelectedSectionId(sectionId)
    setSelectedQuestionId('')
    setNewQuestionIndex(index + 1)
  }

  const handleMoveQuestion = (
    question: QuestionSchema,
    currentQuestionIndex: number,
    currentSectionIndex: number,
    newSectionIndex: number
  ) => {
    const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))

    updatedForm.sections[currentSectionIndex].questions.splice(currentQuestionIndex, 1)

    updatedForm.sections[newSectionIndex].questions
      ? updatedForm.sections[newSectionIndex].questions.splice(
          updatedForm.sections[newSectionIndex].questions.length,
          0,
          question
        )
      : (updatedForm.sections[newSectionIndex].questions = [question])

    dispatch(setPayload({ ...payload, form: updatedForm }))
    resetSelectedQuestion()
  }

  const handleDuplicateSection = (section: SectionSchema, sectionIndex: number) => {
    const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))
    const newSection = JSON.parse(JSON.stringify(section))

    newSection.id = `SID_${Date.now()}`
    newSection.questions = []

    updatedForm.sections.splice(sectionIndex + 1, 0, newSection)

    dispatch(setPayload({ ...payload, form: updatedForm }))
    resetSelectedSection()
  }

  const handleDuplicateQuestion = (
    question: QuestionSchema,
    sectionIndex: number,
    questionIndex: number
  ) => {
    const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))
    const newQuestion = JSON.parse(JSON.stringify(question))

    newQuestion.id = `QID_${Date.now()}`

    updatedForm.sections[sectionIndex].questions.splice(questionIndex + 1, 0, newQuestion)

    dispatch(setPayload({ ...payload, form: updatedForm }))
    resetSelectedQuestion()
  }

  const handleResetSection = (sectionIndex: number) => {
    if (window.confirm('Confirm reset? All questions in section will be deleted.')) {
      const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))

      updatedForm.sections[sectionIndex].questions = []

      dispatch(setPayload({ ...payload, form: updatedForm }))
      resetSelectedSection()
    }
  }

  const handleDeleteSection = (section: SectionSchema) => {
    if (
      window.confirm(
        `Confirm delete? ${section.questions.length ? 'Questions in section will also be deleted.' : ''}`
      )
    ) {
      const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))

      const sectionIndex = updatedForm.sections.findIndex(({ id }) => id === section.id)
      updatedForm.sections.splice(sectionIndex, 1)

      dispatch(setPayload({ ...payload, form: updatedForm }))
      resetSelectedSection()
    }
  }

  const handleDeleteQuestion = (questionId: string, sectionId: string) => {
    if (window.confirm('Confirm delete?')) {
      const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))

      const sectionIndex = updatedForm.sections.findIndex(({ id }) => id === sectionId)
      const questionIndex = updatedForm.sections[sectionIndex].questions.findIndex(
        ({ id }) => id === questionId
      )

      updatedForm.sections[sectionIndex].questions.splice(questionIndex, 1)

      dispatch(setPayload({ ...payload, form: updatedForm }))
      resetSelectedQuestion()
    }
  }

  const handleSaveSection = (section: SectionSchema, newSectionIndex: number) => {
    const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))

    if (section.id) {
      const sectionIndex = updatedForm.sections.findIndex(({ id }) => id === section.id)
      updatedForm.sections.splice(sectionIndex, 1)
      updatedForm.sections.splice(newSectionIndex, 0, section)
    } else {
      section.id = `SID_${Date.now()}`
      updatedForm.sections.splice(newSectionIndex, 0, section)
    }

    dispatch(setPayload({ ...payload, form: updatedForm }))
    resetSelectedSection()
  }

  const handleSaveQuestion = (
    question: QuestionSchema,
    sectionId: string,
    newQuestionIndex: number
  ) => {
    const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))
    const updatedQuestion: QuestionSchema = JSON.parse(JSON.stringify(question))

    const sectionIndex = updatedForm.sections.findIndex(({ id }) => id === sectionId)

    if (updatedQuestion.id) {
      const questionIndex = updatedForm.sections[sectionIndex].questions.findIndex(
        ({ id }) => id === updatedQuestion.id
      )

      if (question.type === 'text' || question.type === 'textarea') {
        updatedQuestion.meta = {
          textOptions: question.meta?.textOptions ?? {}
        }

        if (question.meta?.placeholder) {
          updatedQuestion.meta = {
            ...updatedQuestion.meta,
            placeholder: question.meta.placeholder
          }
        }
      }

      if (question.type === 'number') {
        updatedQuestion.meta = {
          numberOptions: question.meta?.numberOptions ?? {}
        }

        if (question.meta?.placeholder) {
          updatedQuestion.meta = {
            ...updatedQuestion.meta,
            placeholder: question.meta.placeholder
          }
        }
      }

      if (question.type === 'date') {
        updatedQuestion.meta = {
          dateOptions: question.meta?.dateOptions ?? {}
        }
      }

      if (question.type === 'dropdown') {
        updatedQuestion.meta = {
          dropdownOptions: {
            values: question.meta?.dropdownOptions?.values ?? []
          }
        }
      }

      if (question.type === 'checkbox') {
        updatedQuestion.meta = {
          checkboxOptions: question.meta?.checkboxOptions ?? { values: [] }
        }
      }

      if (newQuestionIndex === questionIndex) {
        updatedForm.sections[sectionIndex].questions[questionIndex] = updatedQuestion
      } else {
        updatedForm.sections[sectionIndex].questions.splice(questionIndex, 1)
        updatedForm.sections[sectionIndex].questions.splice(newQuestionIndex, 0, updatedQuestion)
      }
    } else {
      updatedQuestion.id = `QID_${Date.now()}`

      updatedForm.sections[sectionIndex].questions
        ? updatedForm.sections[sectionIndex].questions.splice(newQuestionIndex, 0, updatedQuestion)
        : (updatedForm.sections[sectionIndex].questions = [updatedQuestion])
    }

    dispatch(setPayload({ ...payload, form: updatedForm }))

    setAllQuestions([...allQuestions, updatedQuestion])
    resetSelectedQuestion()
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    let questionsPresent = false
    let isQuestionRequired = false

    for (const section of payload.form.sections) {
      if (section.questions.length) {
        if (!questionsPresent) {
          questionsPresent = true
        }
      } else {
        setRequestState({
          ...requestState,
          warning: `Section "${section.name || 'Untitled section'}" does not have any questions`
        })
        return
      }

      if (!isQuestionRequired) {
        for (const question of section.questions) {
          if (question.required) {
            isQuestionRequired = true
            break
          }
        }
      }
    }

    if (!questionsPresent) {
      setRequestState({
        ...requestState,
        warning: 'Add at least one section and question to advance to preview'
      })
      return
    }

    if (!isQuestionRequired) {
      setRequestState({
        ...requestState,
        warning: 'Form should have at least one required question'
      })
      return
    }

    let url = isNewForm ? `/form/new/preview` : `/form/${formId}/preview?edit=1`

    if (isAddingTemplate) {
      url += '?action=__addTemplate'
    }

    if (isEditingTemplate) {
      url += '&action=__editTemplate'
    }

    if (payload.form.description === 'Add description...') {
      const updatedForm: FormSchema = JSON.parse(JSON.stringify(payload.form))
      delete updatedForm.description
      dispatch(setPayload({ ...payload, form: updatedForm }))
    }

    navigate(url, { viewTransition: true })
  }

  const handleResetForm = () => {
    if (window.confirm('Reset progress?')) {
      dispatch(resetPayload())
      setRequestState({ error: '', warning: '' })
      removeThemeClass()
      addThemeClass('default')
    }
  }

  const showSectionWizard = (): boolean => {
    return isSectionWizardOpen && !selectedSectionId
  }

  const showQuestionWizard = (sectionId: string): boolean => {
    return isQuestionWizardOpen && !selectedQuestionId && selectedSectionId === sectionId
  }

  const resetSelectedSection = () => {
    setSelectedSectionId('')
    isSectionWizardOpen && setIsSectionWizardOpen(false)
  }

  const resetSelectedQuestion = () => {
    setSelectedSectionId('')
    setSelectedQuestionId('')
    isQuestionWizardOpen && setIsQuestionWizardOpen(false)
    isSecondaryContextMenuOpen && setIsSecondaryContextMenuOpen(false)
  }

  const updateFormTips = (index: number) => {
    const updatedFormTips = [...formTips]
    const tip = updatedFormTips[index]
    tip.expanded = !tip.expanded
    setFormTips(updatedFormTips)
  }

  /* const findQuestion = (questionId: string) =>
    allQuestions.find(({ id }) => id === questionId) */

  return (
    <>
      {isLoading ? (
        <Spinner size='center' />
      ) : (
        <div className='main_content form_content'>
          <NavBack theme={payload.form.meta.colour} />
          {requestState.warning && (
            <Status
              warning={requestState.warning}
              reset={() => setRequestState({ ...requestState, warning: '' })}
            />
          )}
          {requestState.error ? (
            <>
              {requestState.error === 'no_access' ? (
                <Status warning='Form cannot be accessed' />
              ) : (
                <Status error={requestState.error} />
              )}
            </>
          ) : (
            <form onSubmit={handleSubmit} ref={formRef}>
              <header className='heading-edit'>
                {isEditingHeader.name ? (
                  <input
                    id='name'
                    defaultValue={payload.form.name}
                    onChange={handleFormChange}
                    onBlur={() => setIsEditingHeader({ ...isEditingHeader, name: false })}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                    placeholder='Enter title...'
                  />
                ) : (
                  <h1
                    className='heading-styled'
                    onClick={() => setIsEditingHeader({ ...isEditingHeader, name: true })}>
                    {payload.form.name}
                  </h1>
                )}
                {isEditingHeader.description ? (
                  <textarea
                    id='description'
                    defaultValue={payload.form.description}
                    onChange={handleFormChange}
                    onBlur={() =>
                      setIsEditingHeader({
                        ...isEditingHeader,
                        description: false
                      })
                    }
                    rows={TEXT_AREA_ROWS_MIN}
                    onFocus={(e) => e.target.select()}
                    autoFocus
                    placeholder='Enter description...'
                  />
                ) : (
                  <p
                    onClick={() =>
                      setIsEditingHeader({
                        ...isEditingHeader,
                        description: true
                      })
                    }>
                    {payload.form.description}
                  </p>
                )}
              </header>
              <div className='actions_container'>
                <div className='actions-select actions-select-fg'>
                  <span className='material-symbols-rounded material-symbols-sm'>
                    {payload.form.isPublic ? 'public' : 'public_off'}
                  </span>
                  <div className='actions_series'>
                    <div>
                      <div
                        id='form_options_trigger'
                        className='div-with-icon div-with-icon-pd'
                        onClick={() => setIsFormOptionsOpen(!isFormOptionsOpen)}
                        ref={formOptionsTriggerRef}>
                        <span id='form_options_trigger'>
                          {payload.form.isPublic ? 'Public' : 'Private'}
                        </span>
                        <span id='form_options_trigger' className='material-symbols-rounded'>
                          {isFormOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                        </span>
                      </div>
                      {isFormOptionsOpen && (
                        <div
                          className='context_menu context_menu-left context_menu-down context_menu-wrap context_menu-grid context_menu-grid-alt'
                          ref={formOptionsContextRef}>
                          <input
                            id='isPublic'
                            type='checkbox'
                            checked={payload.form.isPublic}
                            onChange={handleFormIsPublic}
                            hidden
                          />
                          <label
                            htmlFor='isPublic'
                            className={`${payload.form.isPublic ? 'context_menu-grid-selected' : ''}`}>
                            <div>
                              <div>
                                <span className='material-symbols-rounded'>public</span>
                                <h3>Public</h3>
                              </div>
                              <span>Make surveys that you can share with others</span>
                            </div>
                          </label>
                          <input
                            id='isPrivate'
                            type='checkbox'
                            checked={!payload.form.isPublic}
                            onChange={handleFormIsPublic}
                            hidden
                          />
                          <label
                            htmlFor='isPrivate'
                            className={`${!payload.form.isPublic ? 'context_menu-grid-selected' : ''}`}>
                            <div>
                              <div>
                                <span className='material-symbols-rounded'>public_off</span>
                                <h3>Private</h3>
                              </div>
                              <span>Forms that accept responses from you</span>
                            </div>
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className='actions-options'>
                    {payload.form.isPublic && !(isAddingTemplate || isEditingTemplate) && (
                      <div className='checkbox_container checkbox_container-alt'>
                        <div>
                          <input
                            id='publish'
                            type='checkbox'
                            checked={!!payload.form.publishedOn}
                            onChange={handlePublishForm}
                            hidden
                          />
                          <span
                            className={`material-symbols-rounded ${payload.form.publishedOn ? 'on' : ''}`}
                            onClick={handlePublishForm}>
                            {payload.form.publishedOn ? 'toggle_on' : 'toggle_off'}
                          </span>
                          <label htmlFor='publish'>Publish</label>
                        </div>
                      </div>
                    )}
                    <div
                      id='palette_trigger'
                      className='theme-content'
                      onClick={() => setIsPaletteOpen(!isPaletteOpen)}
                      ref={paletteTriggerRef}>
                      <div
                        id='palette_trigger'
                        className='theme-content-selected'
                        style={{
                          backgroundColor: `${palettesData.find(({ name }) => name === payload.form.meta.colour)?.hex}`
                        }}
                        title={capitalize(payload.form.meta.colour)}
                      />
                      <div className='theme-content-menu'>
                        <span
                          id='palette_trigger'
                          className='material-symbols-rounded material-symbols-sm'>
                          palette
                        </span>
                        {isPaletteOpen && (
                          <div className='context_menu' ref={paletteContextRef}>
                            <div className='palette-grid'>
                              {palettesData.map(({ name, hex }) => (
                                <div
                                  style={{ backgroundColor: `${hex}` }}
                                  key={name}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setPaletteValue(name)
                                  }}
                                  title={capitalize(name)}>
                                  {payload.form.meta.colour === name && (
                                    <span className='material-symbols-rounded'>check</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {payload.form.sections.map((section, sectionIndex) => {
                sumCheckboxQuestions.current = 0

                return (
                  <section key={section.id}>
                    {newSectionIndex === sectionIndex && showSectionWizard() && (
                      <SectionWizard
                        form={payload.form}
                        newSectionIndex={newSectionIndex}
                        save={handleSaveSection}
                        reset={resetSelectedSection}
                      />
                    )}
                    {isSectionWizardOpen && selectedSectionId === section.id ? (
                      <SectionWizard
                        form={payload.form}
                        section={section}
                        newSectionIndex={sectionIndex}
                        save={handleSaveSection}
                        reset={resetSelectedSection}
                      />
                    ) : (
                      <div className='header'>
                        {section.meta?.showHeader && (
                          <>
                            {section.name && <h2>{section.name}</h2>}
                            {section.description && <p>{section.description}</p>}
                          </>
                        )}
                        <div
                          className={`options ${section.meta?.showHeader ? '' : 'options-no-section'}`}>
                          <span
                            id='options_trigger'
                            className={`material-symbols-rounded options_trigger ${
                              isSectionWizardOpen || isQuestionWizardOpen ? 'disabled' : ''
                            }`}
                            onClick={() => handleSelectSection(section.id)}
                            ref={selectedSectionTriggerRef}>
                            more_vert
                          </span>
                          {!isQuestionWizardOpen && selectedSectionId === section.id && (
                            <div
                              className='context_menu context_menu-right context_menu-wrap'
                              ref={selectedSectionContextRef}>
                              <div
                                className='div-with-icon highlight-styled-hover'
                                onClick={() => setIsSectionWizardOpen(true)}>
                                <span className='material-symbols-rounded'>edit</span>
                                <span>Edit section</span>
                              </div>
                              <div
                                className='div-with-icon highlight-styled-hover'
                                onClick={() => handleDuplicateSection(section, sectionIndex)}>
                                <span className='material-symbols-rounded'>content_copy</span>
                                <span>Duplicate</span>
                              </div>
                              <hr />
                              <div
                                className='div-with-icon highlight-styled-hover'
                                onClick={() => handleInsertSectionAbove(sectionIndex)}>
                                <span className='material-symbols-rounded'>add</span>
                                <span>Insert above</span>
                              </div>
                              <div
                                className='div-with-icon highlight-styled-hover'
                                onClick={() => handleInsertSectionBelow(sectionIndex)}>
                                <span className='material-symbols-rounded'>add</span>
                                <span>Insert below</span>
                              </div>
                              <hr />
                              <div
                                className={`div-with-icon highlight-styled-hover ${!sectionIndex ? 'disabled' : ''}`}
                                onClick={() => handleSaveSection(section, 0)}>
                                <span className='material-symbols-rounded'>vertical_align_top</span>
                                <span>Move to top</span>
                              </div>
                              <div
                                className={`div-with-icon highlight-styled-hover ${!sectionIndex ? 'disabled' : ''}`}
                                onClick={() => handleSaveSection(section, sectionIndex - 1)}>
                                <span className='material-symbols-rounded'>arrow_upward_alt</span>
                                <span>Move up</span>
                              </div>
                              <div
                                className={`div-with-icon highlight-styled-hover 
                          ${sectionIndex === payload.form.sections.length - 1 ? 'disabled' : ''}`}
                                onClick={() => handleSaveSection(section, sectionIndex + 1)}>
                                <span className='material-symbols-rounded'>arrow_downward_alt</span>
                                <span>Move down</span>
                              </div>
                              <div
                                className={`div-with-icon highlight-styled-hover 
                          ${sectionIndex === payload.form.sections.length - 1 ? 'disabled' : ''}`}
                                onClick={() =>
                                  handleSaveSection(section, payload.form.sections.length - 1)
                                }>
                                <span className='material-symbols-rounded'>
                                  vertical_align_bottom
                                </span>
                                <span>Move to bottom</span>
                              </div>
                              <hr />
                              <div
                                className={`div-with-icon error ${!section.questions.length ? 'disabled' : ''}`}
                                onClick={() => handleResetSection(sectionIndex)}>
                                <span className='material-symbols-rounded'>refresh</span>
                                <span>Reset questions</span>
                              </div>
                              <div
                                className='div-with-icon error'
                                onClick={() => handleDeleteSection(section)}>
                                <span className='material-symbols-rounded'>delete</span>
                                <span>Delete section</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {section.questions.map((question, questionIndex) => {
                      if (question.type === 'radio_checkbox') {
                        sumCheckboxQuestions.current++
                      }

                      return (
                        <Fragment key={question.id}>
                          {newQuestionIndex === questionIndex && showQuestionWizard(section.id) && (
                            <QuestionWizard
                              section={section}
                              newQuestionIndex={newQuestionIndex}
                              save={handleSaveQuestion}
                              reset={resetSelectedQuestion}
                            />
                          )}
                          {isQuestionWizardOpen && selectedQuestionId === question.id ? (
                            <QuestionWizard
                              section={section}
                              question={question}
                              newQuestionIndex={questionIndex}
                              save={handleSaveQuestion}
                              reset={resetSelectedQuestion}
                            />
                          ) : (
                            <div className='section_question section_question-options'>
                              {question.type === 'radio_checkbox' ? (
                                <div />
                              ) : (
                                <span>
                                  {questionIndex +
                                    1 +
                                    Number(
                                      `${isQuestionWizardOpen && selectedSectionId === section.id && newQuestionIndex <= questionIndex ? 1 : 0}`
                                    ) -
                                    sumCheckboxQuestions.current}
                                  .
                                </span>
                              )}
                              <div>
                                {question.type === 'radio_checkbox' ? (
                                  <div className='checkbox_container checkbox_container-radio checkbox_container-bg'>
                                    <div className='radio-custom radio-custom-preview'>
                                      <input type='checkbox' />
                                      <span />
                                      <label>
                                        <span>{question.statement}</span>
                                        {question.description && <em> ({question.description})</em>}
                                        {question.required && <span className='required'>*</span>}
                                      </label>
                                    </div>
                                  </div>
                                ) : (
                                  <p>
                                    <span className='bold'>{question.statement}</span>
                                    {question.description && <em> ({question.description})</em>}
                                    {question.required && <span className='required'>*</span>}
                                  </p>
                                )}
                              </div>
                              <div className='options'>
                                <div>
                                  <span
                                    id='options_trigger2'
                                    className={`material-symbols-rounded options_trigger ${
                                      isSectionWizardOpen || isQuestionWizardOpen ? 'disabled' : ''
                                    }`}
                                    onClick={() => handleSelectQuestion(question.id)}
                                    ref={selectedQuestionTriggerRef}>
                                    more_vert
                                  </span>
                                  {selectedQuestionId === question.id && (
                                    <div
                                      className='context_menu context_menu-right context_menu-wrap context_menu-split'
                                      ref={selectedQuestionContextRef}>
                                      {isSecondaryContextMenuOpen && (
                                        <>
                                          <div className='context_menu-sec'>
                                            <h4>
                                              <span>Move to section</span>
                                              <span className='material-symbols-rounded'>
                                                exit_to_app
                                              </span>
                                            </h4>
                                            <div>
                                              {payload.form.sections.map(
                                                ({ id, name }, newIndex) => {
                                                  return id === section.id ? (
                                                    <Fragment key={id} />
                                                  ) : (
                                                    <Fragment key={id}>
                                                      <span>●</span>
                                                      <span
                                                        className='highlight-styled-hover'
                                                        onClick={() =>
                                                          handleMoveQuestion(
                                                            question,
                                                            questionIndex,
                                                            sectionIndex,
                                                            newIndex
                                                          )
                                                        }>
                                                        {name || 'Untitled section'}
                                                      </span>
                                                    </Fragment>
                                                  )
                                                }
                                              )}
                                            </div>
                                          </div>
                                          <hr />
                                        </>
                                      )}
                                      <div>
                                        <div
                                          className='div-with-icon highlight-styled-hover'
                                          onClick={() => {
                                            handleRequireQuestion(question.id)
                                          }}>
                                          <span
                                            className={`material-symbols-rounded ${question.required ? 'on' : ''}`}>
                                            {question.required ? 'toggle_on' : 'toggle_off'}
                                          </span>
                                          <span>Required</span>
                                        </div>
                                        <hr />
                                        <div
                                          className='div-with-icon highlight-styled-hover'
                                          onClick={() => setIsQuestionWizardOpen(true)}>
                                          <span className='material-symbols-rounded'>edit</span>
                                          <span>Edit question</span>
                                        </div>
                                        <div
                                          className='div-with-icon highlight-styled-hover'
                                          onClick={() =>
                                            handleDuplicateQuestion(
                                              question,
                                              sectionIndex,
                                              questionIndex
                                            )
                                          }>
                                          <span className='material-symbols-rounded'>
                                            content_copy
                                          </span>
                                          <span>Duplicate</span>
                                        </div>
                                        <hr />
                                        <div
                                          className='div-with-icon highlight-styled-hover'
                                          onClick={() =>
                                            handleInsertQuestionAbove(section.id, questionIndex)
                                          }>
                                          <span className='material-symbols-rounded'>add</span>
                                          <span>Insert above</span>
                                        </div>
                                        <div
                                          className='div-with-icon highlight-styled-hover'
                                          onClick={() =>
                                            handleInsertQuestionBelow(section.id, questionIndex)
                                          }>
                                          <span className='material-symbols-rounded'>add</span>
                                          <span>Insert below</span>
                                        </div>
                                        <hr />
                                        <div
                                          className={`div-with-icon highlight-styled-hover ${!questionIndex ? 'disabled' : ''}`}
                                          onClick={() =>
                                            handleSaveQuestion(question, section.id, 0)
                                          }>
                                          <span className='material-symbols-rounded'>
                                            vertical_align_top
                                          </span>
                                          <span>Move to top</span>
                                        </div>
                                        <div
                                          className={`div-with-icon highlight-styled-hover ${!questionIndex ? 'disabled' : ''}`}
                                          onClick={() =>
                                            handleSaveQuestion(
                                              question,
                                              section.id,
                                              questionIndex - 1
                                            )
                                          }>
                                          <span className='material-symbols-rounded'>
                                            arrow_upward_alt
                                          </span>
                                          <span>Move up</span>
                                        </div>
                                        <div
                                          className={`div-with-icon highlight-styled-hover ${questionIndex === section.questions.length - 1 ? 'disabled' : ''}`}
                                          onClick={() =>
                                            handleSaveQuestion(
                                              question,
                                              section.id,
                                              questionIndex + 1
                                            )
                                          }>
                                          <span className='material-symbols-rounded'>
                                            arrow_downward_alt
                                          </span>
                                          <span>Move down</span>
                                        </div>
                                        <div
                                          className={`div-with-icon highlight-styled-hover ${questionIndex === section.questions.length - 1 ? 'disabled' : ''}`}
                                          onClick={() =>
                                            handleSaveQuestion(
                                              question,
                                              section.id,
                                              section.questions.length - 1
                                            )
                                          }>
                                          <span className='material-symbols-rounded'>
                                            vertical_align_bottom
                                          </span>
                                          <span>Move to bottom</span>
                                        </div>
                                        <hr />
                                        <div
                                          className={`div-with-icon ${isSecondaryContextMenuOpen ? 'highlight-styled' : ''} highlight-styled-hover ${payload.form.sections.length > 1 ? '' : 'disabled'}`}
                                          onClick={() =>
                                            setIsSecondaryContextMenuOpen(
                                              !isSecondaryContextMenuOpen
                                            )
                                          }>
                                          <span className='material-symbols-rounded'>
                                            menu_open
                                          </span>
                                          <span>Move to section</span>
                                        </div>
                                        <hr />
                                        <div
                                          className='div-with-icon error'
                                          onClick={() =>
                                            handleDeleteQuestion(question.id, section.id)
                                          }>
                                          <span className='material-symbols-rounded'>delete</span>
                                          <span>Delete question</span>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              {question.type === 'text' ? (
                                <input
                                  inert
                                  placeholder={question.meta?.placeholder ?? 'Short text...'}
                                />
                              ) : question.type === 'textarea' ? (
                                <textarea
                                  rows={TEXT_AREA_ROWS}
                                  inert
                                  placeholder={question.meta?.placeholder ?? 'Paragraph...'}
                                />
                              ) : question.type === 'number' ? (
                                <input
                                  type='number'
                                  inert
                                  placeholder={question.meta?.placeholder ?? 'Enter number...'}
                                />
                              ) : question.type === 'email' ? (
                                <input
                                  type='email'
                                  inert
                                  placeholder={question.meta?.placeholder ?? 'Enter email...'}
                                />
                              ) : question.type === 'tel' ? (
                                <input
                                  type='tel'
                                  inert
                                  placeholder={question.meta?.placeholder ?? 'Enter phone...'}
                                />
                              ) : question.type === 'date' ? (
                                <input type='date' inert />
                              ) : question.type === 'dropdown' ? (
                                <div className='checkbox_container'>
                                  {question.meta?.dropdownOptions?.values.map(
                                    (option, optionIndex) => (
                                      <div
                                        className='radio-custom radio-custom-preview'
                                        key={optionIndex}>
                                        <input type='radio' />
                                        <span />
                                        <label>{option}</label>
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : question.type === 'checkbox' ? (
                                <div className='checkbox_container'>
                                  {question.meta?.checkboxOptions?.values.map(
                                    (option, optionIndex) => (
                                      <div
                                        className='radio-custom radio-custom-preview'
                                        key={optionIndex}>
                                        <input type='checkbox' />
                                        <span />
                                        <label>{option}</label>
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : question.type === 'rating' ? (
                                <div className='checkbox_container rating-picker'>
                                  {question.meta?.ratingOptions?.map((rating) => (
                                    <div
                                      className='radio-custom radio-custom-preview'
                                      key={rating}
                                      title={`${rating} / ${question.meta?.ratingOptions?.length}`}>
                                      <input type='checkbox' />
                                      <span />
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                question.type === 'file' && <input type='file' />
                              )}
                              {/* {question.conditional && (
                                <>
                                  <div />
                                  <span className='highlight'>
                                    Condition: Show if value of
                                    <em className='bold'>{` "${
                                      findQuestion(question.conditional.questionId)
                                        ?.statement
                                    }"`}</em>
                                    {' is '}
                                    {`${
                                      question.conditional.condition === '>'
                                        ? 'greater'
                                        : 'less'
                                    } than ${question.conditional.value}`}
                                  </span>
                                </>
                              )} */}
                              {(question.type === 'text' || question.type === 'textarea') &&
                                !!Object.keys(question?.meta?.textOptions || {}).length && (
                                  <>
                                    <div className='section_question-subtext'>
                                      {question.meta?.textOptions?.minlength !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Min length: </em>
                                          {formatNum(question.meta.textOptions.minlength)}{' '}
                                          characters
                                        </div>
                                      )}
                                      {question.meta?.textOptions?.maxlength !== undefined && (
                                        <div className='section_question-subtext'>
                                          <div className='highlight'>
                                            <span>● </span>
                                            <em className='bold'>Max length: </em>
                                            {formatNum(question.meta.textOptions.maxlength)}{' '}
                                            characters
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              {question.type === 'number' &&
                                !!Object.keys(question?.meta?.numberOptions || {}).length && (
                                  <>
                                    <div className='section_question-subtext'>
                                      {question.meta?.numberOptions?.unit && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Unit: </em>
                                          {question.meta.numberOptions.unit}
                                        </div>
                                      )}
                                      {question.meta?.numberOptions?.min !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Min: </em>
                                          {formatNum(question.meta.numberOptions.min)}
                                        </div>
                                      )}
                                      {question.meta?.numberOptions?.max !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Max: </em>
                                          {formatNum(question.meta.numberOptions.max)}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              {question.type === 'email' &&
                                !!Object.keys(question?.meta?.emailOptions || {}).length && (
                                  <>
                                    <div className='section_question-subtext'>
                                      {question.meta?.emailOptions?.minlength !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Min length: </em>
                                          {formatNum(question.meta.emailOptions.minlength)}
                                        </div>
                                      )}
                                      {question.meta?.emailOptions?.maxlength !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Max length: </em>
                                          {formatNum(question.meta.emailOptions.maxlength)}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              {question.type === 'tel' &&
                                !!Object.keys(question?.meta?.telOptions || {}).length && (
                                  <>
                                    <div className='section_question-subtext'>
                                      {question.meta?.telOptions?.minlength !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Min length: </em>
                                          {formatNum(question.meta.telOptions.minlength)}
                                        </div>
                                      )}
                                      {question.meta?.telOptions?.maxlength !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Max length: </em>
                                          {formatNum(question.meta.telOptions.maxlength)}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              {question.type === 'date' &&
                                !!Object.keys(question?.meta?.dateOptions || {}).length && (
                                  <>
                                    <div className='section_question-subtext'>
                                      {question.meta?.dateOptions?.min && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>From: </em>
                                          {parseDate(question.meta.dateOptions.min, 'medium_date')}
                                        </div>
                                      )}
                                      {question.meta?.dateOptions?.max && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>To: </em>
                                          {parseDate(question.meta.dateOptions.max, 'medium_date')}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              {question.type === 'checkbox' &&
                                Object.keys(question?.meta?.checkboxOptions || {}).length > 1 && (
                                  <>
                                    <div className='section_question-subtext'>
                                      {question.meta?.checkboxOptions?.minOptions !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Min : </em>
                                          {formatNum(question.meta.checkboxOptions.minOptions)}{' '}
                                          options
                                        </div>
                                      )}
                                      {question.meta?.checkboxOptions?.maxOptions !== undefined && (
                                        <div className='highlight'>
                                          <span>● </span>
                                          <em className='bold'>Max : </em>
                                          {formatNum(question.meta.checkboxOptions.maxOptions)}{' '}
                                          options
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                            </div>
                          )}
                        </Fragment>
                      )
                    })}
                    {newQuestionIndex === section.questions.length &&
                      showQuestionWizard(section.id) && (
                        <QuestionWizard
                          section={section}
                          newQuestionIndex={newQuestionIndex}
                          save={handleSaveQuestion}
                          reset={resetSelectedQuestion}
                        />
                      )}
                    {!showQuestionWizard(section.id) && (
                      <button
                        type='button'
                        className='button-with-icon'
                        onClick={() => handleAddQuestion(section)}
                        disabled={isSectionWizardOpen || isQuestionWizardOpen}>
                        <span className='material-symbols-rounded'>add</span>
                        <span>New question</span>
                      </button>
                    )}
                  </section>
                )
              })}
              {newSectionIndex === payload.form.sections.length && showSectionWizard() && (
                <SectionWizard
                  form={payload.form}
                  newSectionIndex={newSectionIndex}
                  save={handleSaveSection}
                  reset={resetSelectedSection}
                />
              )}
              <div className='section-add'>
                <hr />
                <button
                  type='button'
                  className='button-with-icon cta'
                  onClick={handleAddSection}
                  disabled={isSectionWizardOpen || isQuestionWizardOpen}>
                  <span className='material-symbols-rounded'>add</span>
                  <span>New section</span>
                </button>
                <hr />
              </div>
              <div
                className={`expandable_list expandable_list-alt theme-content-${payload.form.meta.colour}`}>
                <div onClick={() => setIsHelpOpen(!isHelpOpen)}>
                  <span className='material-symbols-rounded'>info</span>
                  <span>Help</span>
                </div>
                {isHelpOpen && (
                  <ul>
                    {formTips.map((formTip, index) => (
                      <li key={index}>
                        <h4>{index + 1}.</h4>
                        <div onClick={() => updateFormTips(index)}>
                          <h4>{formTip.heading}</h4>
                          <span className='material-symbols-rounded'>
                            {formTip.expanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                          </span>
                        </div>
                        {formTip.expanded && (
                          <>
                            <span />
                            <ul>
                              {formTip.tips.map((tip, index) => (
                                <li key={index}>
                                  <span>●</span>
                                  <div>
                                    <em>{tip.name + '  -  '}</em>
                                    <span>{tip.description}</span>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className='form_footer form_footer-double-stc'>
                {isNewForm && (
                  <div
                    className={`${
                      !isFormDirty() || isSectionWizardOpen || isQuestionWizardOpen
                        ? 'disabled'
                        : ''
                    }`}>
                    <div className='div-with-icon' onClick={handleResetForm}>
                      <span className='material-symbols-rounded'>undo</span>
                      <span>Reset form</span>
                    </div>
                  </div>
                )}
                <button
                  className='submit button-with-icon submit-with-icon'
                  disabled={isSectionWizardOpen || isQuestionWizardOpen}>
                  <span>Preview</span>
                  <span className='material-symbols-rounded'>arrow_right_alt</span>
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </>
  )
}
